import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { Eye, EyeOff, Edit, Trash2, X, Save, MapPin, Calendar, Plus, Camera, Bed, Copy, Star, MessageSquare, Ticket, Users, CalendarRange, Hotel } from 'lucide-react';
import ItineraryEditor from './ItineraryEditor';
import ImageUpload from '../ui/image-upload';
import GalleryUpload from '../ui/gallery-upload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminTripsTab({ trips, token, onRefresh }) {
  const navigate = useNavigate();
  const [editingTrip, setEditingTrip] = useState(null);
  const [editingItinerary, setEditingItinerary] = useState(null);
  const [editingVariantItinerary, setEditingVariantItinerary] = useState(null);
  const [editingAccommodationItinerary, setEditingAccommodationItinerary] = useState(null);
  const [tripForm, setTripForm] = useState({});
  const [newVariantName, setNewVariantName] = useState('');
  const [showVariantInput, setShowVariantInput] = useState(false);
  const [newAccommodationName, setNewAccommodationName] = useState('');
  const [showAccommodationInput, setShowAccommodationInput] = useState(false);

  const handleEditTrip = (trip) => {
    setEditingTrip(trip);
    setTripForm({
      title: trip.title || '',
      slug: trip.slug || '',
      destination: trip.destination || '',
      description: trip.description || '',
      price: trip.price || 0,
      duration_days: trip.duration_days || 7,
      trip_type: trip.trip_type || 'group',
      max_group_size: trip.max_group_size || 15,
      rating: trip.rating || 4.5,
      highlights: trip.highlights?.join('\n') || '',
      included: trip.included?.join('\n') || '',
      not_included: trip.not_included?.join('\n') || '',
      tour_dates: trip.tour_dates || [],
      location_variants: trip.location_variants || [],
      accommodation_levels: trip.accommodation_levels || [],
      single_date_mode: trip.single_date_mode || false,
      date_display_mode: trip.date_display_mode || 'full',
      image_url: trip.image_url || '',
      hero_image_url: trip.hero_image_url || '',
      gallery: trip.gallery || [],
      solo_price_adjustment: trip.solo_price_adjustment || 0,
      room_options: trip.room_options || { shared: true, private: true },
      private_room_supplement: trip.private_room_supplement || 600,
      room_types: trip.room_types || [],
      booking_cutoff_days: trip.booking_cutoff_days || 14,
      add_ons: trip.add_ons || [],
      traveler_pricing: trip.traveler_pricing || [],
      seasonal_pricing: trip.seasonal_pricing || []
    });
  };

  const handleAddTourDate = () => {
    const newDate = document.getElementById('new-tour-date')?.value;
    if (!newDate) {
      toast.error('Please select a date');
      return;
    }
    if (tripForm.tour_dates.includes(newDate)) {
      toast.error('This date is already added');
      return;
    }
    setTripForm({
      ...tripForm,
      tour_dates: [...tripForm.tour_dates, newDate].sort()
    });
    document.getElementById('new-tour-date').value = '';
  };

  const handleRemoveTourDate = (dateToRemove) => {
    setTripForm({
      ...tripForm,
      tour_dates: tripForm.tour_dates.filter(d => d !== dateToRemove)
    });
  };

  // Helper to format date without timezone issues
  const formatTourDate = (dateStr) => {
    // Parse YYYY-MM-DD without timezone conversion
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSaveTrip = async () => {
    try {
      if (!editingTrip?.id) {
        toast.error('Trip ID is missing');
        return null;
      }
      
      // Validate slug
      if (!tripForm.slug || tripForm.slug.trim() === '') {
        toast.error('URL slug is required');
        return null;
      }
      
      const updateData = {
        ...tripForm,
        slug: tripForm.slug.toLowerCase().replace(/[^a-z0-9-]/g, ''),
        price: parseFloat(tripForm.price),
        duration_days: parseInt(tripForm.duration_days),
        max_group_size: parseInt(tripForm.max_group_size),
        rating: parseFloat(tripForm.rating),
        highlights: tripForm.highlights.split('\n').filter(h => h.trim()),
        included: tripForm.included.split('\n').filter(i => i.trim()),
        not_included: tripForm.not_included.split('\n').filter(n => n.trim()),
        tour_dates: tripForm.tour_dates,
        location_variants: tripForm.location_variants,
        accommodation_levels: tripForm.accommodation_levels || [],
        single_date_mode: tripForm.single_date_mode,
        date_display_mode: tripForm.date_display_mode,
        image_url: tripForm.image_url,
        hero_image_url: tripForm.hero_image_url,
        gallery: tripForm.gallery,
        solo_price_adjustment: parseInt(tripForm.solo_price_adjustment) || 0,
        room_options: tripForm.room_options,
        private_room_supplement: parseInt(tripForm.private_room_supplement) || 600,
        room_types: tripForm.room_types || [],
        booking_cutoff_days: parseInt(tripForm.booking_cutoff_days) || 14,
        add_ons: tripForm.add_ons || [],
        traveler_pricing: tripForm.traveler_pricing || [],
        seasonal_pricing: tripForm.seasonal_pricing || []
      };
      
      await axios.put(`${API}/admin/trips/${editingTrip.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Trip updated successfully');
      // Update the local editingTrip with new data for itinerary editors - include updated slug
      const updatedTrip = { 
        ...editingTrip, 
        ...updateData,
        id: editingTrip.id,
        slug: updateData.slug
      };
      setEditingTrip(updatedTrip);
      onRefresh();
      return updatedTrip;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update trip');
      return null;
    }
  };

  const handleDeleteTrip = async (tripId) => {
    if (!window.confirm('Are you sure you want to delete this trip?')) return;
    
    try {
      await axios.delete(`${API}/trips/${tripId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Trip deleted');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete trip');
    }
  };

  const handleToggleVisibility = async (trip) => {
    try {
      const newStatus = !trip.is_active;
      await axios.put(`${API}/admin/trips/${trip.id}`, {
        is_active: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(newStatus ? 'Trip is now visible to customers' : 'Trip is now hidden from customers');
      onRefresh();
    } catch (err) {
      toast.error('Failed to update trip visibility');
    }
  };

  const handleToggleFeatured = async (trip) => {
    try {
      const newFeatured = !trip.is_featured;
      await axios.put(`${API}/admin/trips/${trip.id}`, {
        is_featured: newFeatured
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(newFeatured ? 'Trip is now featured on homepage' : 'Trip removed from featured');
      onRefresh();
    } catch (err) {
      toast.error('Failed to update featured status');
    }
  };

  const handleToggleReviews = async (trip) => {
    try {
      const newShowReviews = !trip.show_reviews;
      await axios.put(`${API}/admin/trips/${trip.id}`, {
        show_reviews: newShowReviews
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(newShowReviews ? 'Reviews are now visible for this trip' : 'Reviews hidden for this trip');
      onRefresh();
    } catch (err) {
      toast.error('Failed to update reviews setting');
    }
  };

  const handleCloneTrip = async (trip) => {
    if (!window.confirm(`Clone "${trip.title}" including its itinerary?`)) return;
    
    try {
      const res = await axios.post(`${API}/admin/trips/${trip.id}/clone`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Trip cloned! New trip: "${res.data.trip.title}"`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clone trip');
    }
  };

  // If editing itinerary, show the editor
  if (editingItinerary) {
    return (
      <ItineraryEditor
        trip={editingItinerary}
        onBack={() => setEditingItinerary(null)}
        onSave={() => {
          setEditingItinerary(null);
          onRefresh();
        }}
      />
    );
  }

  return (
    <>
      <Card data-testid="admin-trips-tab">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>All Trips</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium text-muted-foreground">Trip</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Destination</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Price</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Featured</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Reviews</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Tour Dates</th>
                  <th className="text-left p-3 font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {trips.map((trip) => (
                  <tr key={trip.id} className={`border-t ${!trip.is_active ? 'bg-gray-50 opacity-60' : ''}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{trip.title}</span>
                        {!trip.is_active && (
                          <Badge variant="outline" className="text-xs bg-gray-100">Hidden</Badge>
                        )}
                      </div>
                    </td>
                    <td className="p-3">{trip.destination}</td>
                    <td className="p-3">${trip.price?.toLocaleString()}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={trip.is_active !== false}
                          onCheckedChange={() => handleToggleVisibility(trip)}
                          data-testid={`toggle-visibility-${trip.id}`}
                        />
                        <span className="text-xs text-muted-foreground">
                          {trip.is_active !== false ? 'Visible' : 'Hidden'}
                        </span>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleFeatured(trip)}
                          className={`p-1.5 rounded-full transition-colors ${
                            trip.is_featured 
                              ? 'bg-secondary text-white' 
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                          title={trip.is_featured ? 'Remove from featured' : 'Add to featured'}
                          data-testid={`toggle-featured-${trip.id}`}
                        >
                          <Star className={`h-4 w-4 ${trip.is_featured ? 'fill-current' : ''}`} />
                        </button>
                        {trip.is_featured && (
                          <span className="text-xs text-secondary font-medium">Featured</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleReviews(trip)}
                          className={`p-1.5 rounded-full transition-colors ${
                            trip.show_reviews 
                              ? 'bg-green-500 text-white' 
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          }`}
                          title={trip.show_reviews ? 'Hide reviews' : 'Show reviews'}
                          data-testid={`toggle-reviews-${trip.id}`}
                        >
                          <MessageSquare className={`h-4 w-4 ${trip.show_reviews ? 'fill-current' : ''}`} />
                        </button>
                        <span className="text-xs text-muted-foreground">
                          {trip.show_reviews ? 'On' : 'Off'}
                        </span>
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge variant="secondary" className="font-mono">
                        {trip.tour_dates?.length || 0} dates
                      </Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/trips/${trip.slug}`)}
                          title="View Trip"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditTrip(trip)}
                          title="Edit Details"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingItinerary(trip)}
                          title="Edit Itinerary"
                          className="text-purple-600 hover:text-purple-700"
                        >
                          <MapPin className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleCloneTrip(trip)}
                          title="Clone Trip"
                          className="text-green-600 hover:text-green-700"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteTrip(trip.id)}
                          className="text-destructive hover:text-destructive"
                          title="Delete Trip"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Edit Trip Modal */}
      {editingTrip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Edit Trip</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setEditingTrip(null)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    value={tripForm.title}
                    onChange={(e) => setTripForm({ ...tripForm, title: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Destination</Label>
                  <Input
                    value={tripForm.destination}
                    onChange={(e) => setTripForm({ ...tripForm, destination: e.target.value })}
                  />
                </div>
              </div>
              
              {/* URL Slug */}
              <div>
                <Label className="flex items-center gap-2">
                  URL Slug
                  <span className="text-xs text-muted-foreground font-normal">(appears in the browser address bar)</span>
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-muted-foreground">/trips/</span>
                  <Input
                    value={tripForm.slug}
                    onChange={(e) => {
                      // Auto-format slug: lowercase, replace spaces with hyphens, remove special chars
                      const formatted = e.target.value
                        .toLowerCase()
                        .replace(/\s+/g, '-')
                        .replace(/[^a-z0-9-]/g, '');
                      setTripForm({ ...tripForm, slug: formatted });
                    }}
                    placeholder="my-trip-name"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Auto-generate slug from title
                      const autoSlug = tripForm.title
                        .toLowerCase()
                        .replace(/[^a-z0-9\s-]/g, '')
                        .replace(/\s+/g, '-')
                        .replace(/-+/g, '-')
                        .trim();
                      setTripForm({ ...tripForm, slug: autoSlug });
                      toast.success('Slug generated from title');
                    }}
                  >
                    Auto-generate
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Full URL: {process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin}/trips/{tripForm.slug || 'your-trip-slug'}
                </p>
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={tripForm.description}
                  onChange={(e) => setTripForm({ ...tripForm, description: e.target.value })}
                  rows={3}
                />
              </div>
              
              {/* Trip Images Section */}
              <div className="border-t pt-4">
                <Label className="flex items-center gap-2 mb-3">
                  <Camera className="h-4 w-4 text-secondary" />
                  Trip Images
                </Label>
                
                <div className="space-y-4">
                  {/* Main Image */}
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Main Image (Card Thumbnail)</Label>
                    <ImageUpload
                      value={tripForm.image_url}
                      onChange={(url) => setTripForm({ ...tripForm, image_url: url })}
                      token={token}
                      placeholder="https://example.com/main-image.jpg"
                      previewClassName="w-32 h-20"
                    />
                  </div>
                  
                  {/* Hero Image */}
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Hero Image (Trip Detail Banner)</Label>
                    <ImageUpload
                      value={tripForm.hero_image_url}
                      onChange={(url) => setTripForm({ ...tripForm, hero_image_url: url })}
                      token={token}
                      placeholder="https://example.com/hero-image.jpg"
                      previewClassName="w-48 h-24"
                    />
                  </div>
                  
                  {/* Gallery */}
                  <div>
                    <Label className="text-xs text-muted-foreground mb-2 block">Gallery Images</Label>
                    <GalleryUpload
                      images={tripForm.gallery || []}
                      onChange={(gallery) => setTripForm({ ...tripForm, gallery })}
                      token={token}
                    />
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-4 gap-4">
                <div>
                  <Label>Price ($)</Label>
                  <Input
                    type="number"
                    value={tripForm.price}
                    onChange={(e) => setTripForm({ ...tripForm, price: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Duration (days)</Label>
                  <Input
                    type="number"
                    value={tripForm.duration_days}
                    onChange={(e) => setTripForm({ ...tripForm, duration_days: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Booking Cutoff (days before)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={tripForm.booking_cutoff_days}
                    onChange={(e) => setTripForm({ ...tripForm, booking_cutoff_days: parseInt(e.target.value) || 14 })}
                    placeholder="14"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Bookings close this many days before departure
                  </p>
                </div>
                <div>
                  <Label>Max Group Size</Label>
                  <Input
                    type="number"
                    value={tripForm.max_group_size}
                    onChange={(e) => setTripForm({ ...tripForm, max_group_size: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Rating</Label>
                  <Input
                    type="number"
                    step="0.1"
                    max="5"
                    value={tripForm.rating}
                    onChange={(e) => setTripForm({ ...tripForm, rating: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Highlights (one per line)</Label>
                <Textarea
                  value={tripForm.highlights}
                  onChange={(e) => setTripForm({ ...tripForm, highlights: e.target.value })}
                  rows={3}
                  placeholder="Visit the Pyramids of Giza&#10;Cruise down the Nile River&#10;Explore ancient temples"
                />
              </div>
              <div>
                <Label>What's Included (one per line)</Label>
                <Textarea
                  value={tripForm.included}
                  onChange={(e) => setTripForm({ ...tripForm, included: e.target.value })}
                  rows={3}
                />
              </div>
              <div>
                <Label>Not Included (one per line)</Label>
                <Textarea
                  value={tripForm.not_included}
                  onChange={(e) => setTripForm({ ...tripForm, not_included: e.target.value })}
                  rows={2}
                />
              </div>
              
              {/* Pricing & Room Options Section */}
              <div className="border-t pt-4">
                <Label className="flex items-center gap-2 mb-3">
                  <span className="text-secondary">💰</span>
                  Pricing & Room Options
                </Label>
                
                <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                  {/* Solo/Custom Date Pricing */}
                  <div>
                    <Label className="text-sm mb-2 block">Solo/Custom Date Price Adjustment</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        value={tripForm.solo_price_adjustment || 0}
                        onChange={(e) => setTripForm({ ...tripForm, solo_price_adjustment: e.target.value })}
                        className="w-32"
                        placeholder="0"
                      />
                      <span className="text-sm text-muted-foreground">
                        {tripForm.solo_price_adjustment > 0 ? '+' : ''}{tripForm.solo_price_adjustment || 0} added for custom dates
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Group price: ${tripForm.price || 0} | Solo price: ${(parseFloat(tripForm.price) || 0) + (parseInt(tripForm.solo_price_adjustment) || 0)}
                    </p>
                  </div>
                  
                  {/* Room Options */}
                  <div>
                    <Label className="text-sm mb-2 block">Room Availability</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tripForm.room_options?.shared !== false}
                          onChange={(e) => setTripForm({ 
                            ...tripForm, 
                            room_options: { ...tripForm.room_options, shared: e.target.checked }
                          })}
                          className="rounded"
                        />
                        <span className="text-sm">Shared Rooms</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tripForm.room_options?.private !== false}
                          onChange={(e) => setTripForm({ 
                            ...tripForm, 
                            room_options: { ...tripForm.room_options, private: e.target.checked }
                          })}
                          className="rounded"
                        />
                        <span className="text-sm">Private Rooms</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Private Room Supplement */}
                  {tripForm.room_options?.private !== false && (
                    <div>
                      <Label className="text-sm mb-2 block">Private Room Supplement ($)</Label>
                      <Input
                        type="number"
                        value={tripForm.private_room_supplement || 600}
                        onChange={(e) => setTripForm({ ...tripForm, private_room_supplement: e.target.value })}
                        className="w-32"
                        placeholder="600"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Additional cost per person for private room
                      </p>
                    </div>
                  )}
                  
                  {/* Room Types Management */}
                  <div className="border-t pt-4 mt-4">
                    <Label className="flex items-center gap-2 mb-3">
                      <Bed className="h-4 w-4 text-secondary" />
                      Shared Room Types
                    </Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      Define room options with descriptions, photos, occupancy, pricing, and availability
                    </p>
                    
                    {/* Existing Room Types */}
                    <div className="space-y-4 mb-3">
                      {(tripForm.room_types || []).map((roomType, idx) => (
                        <div key={roomType.id || idx} className="p-4 bg-muted/30 rounded-lg space-y-3 border border-border/50">
                          {/* Room Name & Delete */}
                          <div className="flex items-center gap-2">
                            <Input
                              value={roomType.name || ''}
                              onChange={(e) => {
                                const updated = [...tripForm.room_types];
                                updated[idx] = { ...updated[idx], name: e.target.value };
                                setTripForm({ ...tripForm, room_types: updated });
                              }}
                              placeholder="Room name (e.g., Double Room)"
                              className="flex-1 text-sm font-medium"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const updated = tripForm.room_types.filter((_, i) => i !== idx);
                                setTripForm({ ...tripForm, room_types: updated });
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          {/* Room Description */}
                          <div>
                            <Label className="text-xs text-muted-foreground mb-1 block">Description</Label>
                            <textarea
                              value={roomType.description || ''}
                              onChange={(e) => {
                                const updated = [...tripForm.room_types];
                                updated[idx] = { ...updated[idx], description: e.target.value };
                                setTripForm({ ...tripForm, room_types: updated });
                              }}
                              placeholder="Describe this room type (amenities, bed configuration, etc.)"
                              className="w-full text-sm p-2 border rounded-md bg-background resize-none"
                              rows={2}
                            />
                          </div>
                          
                          {/* Room Photo */}
                          <div>
                            <Label className="text-xs text-muted-foreground mb-1 block">Room Photo</Label>
                            <div className="flex gap-2 items-start">
                              {roomType.image_url && (
                                <div className="relative w-20 h-20 rounded-md overflow-hidden border flex-shrink-0">
                                  <img 
                                    src={roomType.image_url} 
                                    alt={roomType.name || 'Room'} 
                                    className="w-full h-full object-cover"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = [...tripForm.room_types];
                                      updated[idx] = { ...updated[idx], image_url: '' };
                                      setTripForm({ ...tripForm, room_types: updated });
                                    }}
                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              )}
                              <Input
                                value={roomType.image_url || ''}
                                onChange={(e) => {
                                  const updated = [...tripForm.room_types];
                                  updated[idx] = { ...updated[idx], image_url: e.target.value };
                                  setTripForm({ ...tripForm, room_types: updated });
                                }}
                                placeholder="Image URL (https://...)"
                                className="flex-1 text-sm"
                              />
                            </div>
                          </div>
                          
                          {/* Pricing & Availability Row */}
                          <div className="flex items-center gap-3 flex-wrap">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground">Occupancy:</span>
                              <Input
                                type="number"
                                value={roomType.occupancy || 2}
                                onChange={(e) => {
                                  const updated = [...tripForm.room_types];
                                  updated[idx] = { ...updated[idx], occupancy: parseInt(e.target.value) || 2 };
                                  setTripForm({ ...tripForm, room_types: updated });
                                }}
                                className="w-16 text-sm"
                                min="1"
                                max="10"
                              />
                              <span className="text-xs text-muted-foreground">ppl</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground">Price:</span>
                              <select
                                value={roomType.adjustment_type || 'fixed'}
                                onChange={(e) => {
                                  const updated = [...tripForm.room_types];
                                  updated[idx] = { ...updated[idx], adjustment_type: e.target.value };
                                  setTripForm({ ...tripForm, room_types: updated });
                                }}
                                className="h-9 px-2 text-sm border rounded-md bg-background"
                              >
                                <option value="fixed">$</option>
                                <option value="percentage">%</option>
                              </select>
                              <Input
                                type="number"
                                value={roomType.price_adjustment ?? roomType.price_supplement ?? 0}
                                onChange={(e) => {
                                  const updated = [...tripForm.room_types];
                                  updated[idx] = { 
                                    ...updated[idx], 
                                    price_adjustment: parseInt(e.target.value) || 0,
                                    price_supplement: parseInt(e.target.value) || 0
                                  };
                                  setTripForm({ ...tripForm, room_types: updated });
                                }}
                                className="w-20 text-sm"
                                placeholder="0"
                              />
                              <span className="text-xs text-muted-foreground">
                                {roomType.adjustment_type === 'percentage' ? 'of base' : '/person'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground">Available:</span>
                              <Input
                                type="number"
                                value={roomType.available || 0}
                                onChange={(e) => {
                                  const updated = [...tripForm.room_types];
                                  updated[idx] = { ...updated[idx], available: parseInt(e.target.value) || 0 };
                                  setTripForm({ ...tripForm, room_types: updated });
                                }}
                                className="w-16 text-sm"
                                min="0"
                              />
                            </div>
                          </div>
                          {(roomType.price_adjustment ?? roomType.price_supplement ?? 0) < 0 && (
                            <p className="text-xs text-green-600 bg-green-50 p-1 rounded">
                              💰 This room type gives a {roomType.adjustment_type === 'percentage' 
                                ? `${Math.abs(roomType.price_adjustment ?? 0)}% discount` 
                                : `$${Math.abs(roomType.price_adjustment ?? roomType.price_supplement ?? 0)} discount per person`}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Add New Room Type */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newRoomType = {
                          id: `room_${Date.now()}`,
                          name: '',
                          description: '',
                          image_url: '',
                          occupancy: 2,
                          adjustment_type: 'fixed',
                          price_adjustment: 0,
                          price_supplement: 0,
                          available: 10
                        };
                        setTripForm({
                          ...tripForm,
                          room_types: [...(tripForm.room_types || []), newRoomType]
                        });
                      }}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Room Type
                    </Button>
                    
                    {tripForm.room_types?.length > 0 && (
                      <p className="text-xs text-amber-600 mt-2 bg-amber-50 p-2 rounded">
                        💡 When room types are defined, travelers will choose from these options instead of just "Shared/Private"
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Excursions / Add-ons Section */}
              <div className="border-t pt-4">
                <Label className="flex items-center gap-2 mb-3">
                  <Ticket className="h-4 w-4 text-secondary" />
                  Excursions & Add-ons
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Optional paid activities that travelers can add to their booking
                </p>
                
                <div className="space-y-3">
                  {(tripForm.add_ons || []).map((addon, idx) => (
                    <div key={addon.id || idx} className="p-4 border rounded-lg bg-background">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 grid sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-muted-foreground">Name</Label>
                            <Input
                              value={addon.name || ''}
                              onChange={(e) => {
                                const updated = [...(tripForm.add_ons || [])];
                                updated[idx] = { ...updated[idx], name: e.target.value };
                                setTripForm({ ...tripForm, add_ons: updated });
                              }}
                              placeholder="Hot Air Balloon Ride"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Price ($)</Label>
                            <Input
                              type="number"
                              value={addon.price || ''}
                              onChange={(e) => {
                                const updated = [...(tripForm.add_ons || [])];
                                updated[idx] = { ...updated[idx], price: parseFloat(e.target.value) || 0 };
                                setTripForm({ ...tripForm, add_ons: updated });
                              }}
                              placeholder="150"
                              className="text-sm"
                            />
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const updated = (tripForm.add_ons || []).filter((_, i) => i !== idx);
                            setTripForm({ ...tripForm, add_ons: updated });
                          }}
                          className="text-destructive hover:text-destructive shrink-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="grid sm:grid-cols-2 gap-3 mb-3">
                        <div>
                          <Label className="text-xs text-muted-foreground">Duration</Label>
                          <Input
                            value={addon.duration || ''}
                            onChange={(e) => {
                              const updated = [...(tripForm.add_ons || [])];
                              updated[idx] = { ...updated[idx], duration: e.target.value };
                              setTripForm({ ...tripForm, add_ons: updated });
                            }}
                            placeholder="2 hours"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Image URL (optional)</Label>
                          <Input
                            value={addon.image_url || ''}
                            onChange={(e) => {
                              const updated = [...(tripForm.add_ons || [])];
                              updated[idx] = { ...updated[idx], image_url: e.target.value };
                              setTripForm({ ...tripForm, add_ons: updated });
                            }}
                            placeholder="https://..."
                            className="text-sm"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-xs text-muted-foreground">Description</Label>
                        <Textarea
                          value={addon.description || ''}
                          onChange={(e) => {
                            const updated = [...(tripForm.add_ons || [])];
                            updated[idx] = { ...updated[idx], description: e.target.value };
                            setTripForm({ ...tripForm, add_ons: updated });
                          }}
                          placeholder="Experience the stunning sunrise over the desert from above..."
                          rows={2}
                          className="text-sm"
                        />
                      </div>
                      
                      {addon.price === 0 && (
                        <p className="text-xs text-green-600 mt-2 bg-green-50 p-1 rounded">
                          ✨ This is a FREE excursion
                        </p>
                      )}
                    </div>
                  ))}
                  
                  {/* Add New Excursion */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newAddon = {
                        id: `addon_${Date.now()}`,
                        name: '',
                        description: '',
                        price: 0,
                        duration: '',
                        image_url: ''
                      };
                      setTripForm({
                        ...tripForm,
                        add_ons: [...(tripForm.add_ons || []), newAddon]
                      });
                    }}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Excursion
                  </Button>
                  
                  {(tripForm.add_ons || []).length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2 bg-muted/50 p-2 rounded">
                      💡 {tripForm.add_ons.length} excursion{tripForm.add_ons.length !== 1 ? 's' : ''} will be available for travelers to purchase after booking
                    </p>
                  )}
                </div>
              </div>

              {/* Traveler-Based Pricing Section */}
              <div className="border-t pt-4">
                <Label className="flex items-center gap-2 mb-3">
                  <Users className="h-4 w-4 text-secondary" />
                  Traveler-Based Pricing & Options
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Set different pricing and room availability based on number of travelers
                </p>
                
                <div className="space-y-3">
                  {(tripForm.traveler_pricing || []).map((tp, idx) => (
                    <div key={idx} className="p-4 border rounded-lg bg-background">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 grid sm:grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs text-muted-foreground">Travelers</Label>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                value={tp.min_travelers || 1}
                                onChange={(e) => {
                                  const updated = [...(tripForm.traveler_pricing || [])];
                                  updated[idx] = { ...updated[idx], min_travelers: parseInt(e.target.value) || 1 };
                                  setTripForm({ ...tripForm, traveler_pricing: updated });
                                }}
                                className="text-sm w-16"
                              />
                              <span className="text-sm text-muted-foreground">to</span>
                              <Input
                                type="number"
                                min="1"
                                value={tp.max_travelers || 1}
                                onChange={(e) => {
                                  const updated = [...(tripForm.traveler_pricing || [])];
                                  updated[idx] = { ...updated[idx], max_travelers: parseInt(e.target.value) || 1 };
                                  setTripForm({ ...tripForm, traveler_pricing: updated });
                                }}
                                className="text-sm w-16"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Price Adjustment</Label>
                            <div className="flex items-center gap-1">
                              <select
                                value={tp.adjustment_type || 'dollar'}
                                onChange={(e) => {
                                  const updated = [...(tripForm.traveler_pricing || [])];
                                  updated[idx] = { ...updated[idx], adjustment_type: e.target.value };
                                  setTripForm({ ...tripForm, traveler_pricing: updated });
                                }}
                                className="text-sm border rounded px-2 py-1.5"
                              >
                                <option value="dollar">$</option>
                                <option value="percent">%</option>
                              </select>
                              <Input
                                type="number"
                                value={tp.adjustment_value || 0}
                                onChange={(e) => {
                                  const updated = [...(tripForm.traveler_pricing || [])];
                                  updated[idx] = { ...updated[idx], adjustment_value: parseFloat(e.target.value) || 0 };
                                  setTripForm({ ...tripForm, traveler_pricing: updated });
                                }}
                                placeholder="0"
                                className="text-sm w-24"
                              />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {tp.adjustment_type === 'percent' 
                                ? `${tp.adjustment_value > 0 ? '+' : ''}${tp.adjustment_value || 0}% per person`
                                : `${tp.adjustment_value > 0 ? '+' : ''}$${tp.adjustment_value || 0} per person`}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Fixed Price (optional)</Label>
                            <Input
                              type="number"
                              value={tp.fixed_price || ''}
                              onChange={(e) => {
                                const updated = [...(tripForm.traveler_pricing || [])];
                                updated[idx] = { ...updated[idx], fixed_price: e.target.value ? parseFloat(e.target.value) : null };
                                setTripForm({ ...tripForm, traveler_pricing: updated });
                              }}
                              placeholder="Leave empty to use adjustment"
                              className="text-sm"
                            />
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const updated = (tripForm.traveler_pricing || []).filter((_, i) => i !== idx);
                            setTripForm({ ...tripForm, traveler_pricing: updated });
                          }}
                          className="text-destructive hover:text-destructive shrink-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      {/* Room Types for this traveler count */}
                      <div className="mt-3 pt-3 border-t">
                        <Label className="text-xs text-muted-foreground mb-2 block">Available Room Types</Label>
                        <div className="flex flex-wrap gap-2">
                          {(tripForm.room_types || []).map((rt) => (
                            <label key={rt.id} className="flex items-center gap-1.5 text-sm bg-muted/50 px-2 py-1 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={(tp.allowed_room_types || []).includes(rt.id)}
                                onChange={(e) => {
                                  const updated = [...(tripForm.traveler_pricing || [])];
                                  const currentAllowed = updated[idx].allowed_room_types || [];
                                  if (e.target.checked) {
                                    updated[idx] = { ...updated[idx], allowed_room_types: [...currentAllowed, rt.id] };
                                  } else {
                                    updated[idx] = { ...updated[idx], allowed_room_types: currentAllowed.filter(id => id !== rt.id) };
                                  }
                                  setTripForm({ ...tripForm, traveler_pricing: updated });
                                }}
                                className="rounded"
                              />
                              {rt.name}
                            </label>
                          ))}
                          {(!tripForm.room_types || tripForm.room_types.length === 0) && (
                            <p className="text-xs text-muted-foreground italic">No room types defined. Add room types above first.</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Label/Notes */}
                      <div className="mt-3">
                        <Label className="text-xs text-muted-foreground">Label (shown to travelers)</Label>
                        <Input
                          value={tp.label || ''}
                          onChange={(e) => {
                            const updated = [...(tripForm.traveler_pricing || [])];
                            updated[idx] = { ...updated[idx], label: e.target.value };
                            setTripForm({ ...tripForm, traveler_pricing: updated });
                          }}
                          placeholder="e.g., Solo Traveler Rate, Couple's Package"
                          className="text-sm"
                        />
                      </div>
                    </div>
                  ))}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newTravelerPricing = {
                        id: `tp_${Date.now()}`,
                        min_travelers: 1,
                        max_travelers: 1,
                        adjustment_type: 'dollar',
                        adjustment_value: 0,
                        fixed_price: null,
                        allowed_room_types: [],
                        label: ''
                      };
                      setTripForm({
                        ...tripForm,
                        traveler_pricing: [...(tripForm.traveler_pricing || []), newTravelerPricing]
                      });
                    }}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Traveler Pricing Rule
                  </Button>
                </div>
              </div>

              {/* Seasonal Pricing Section */}
              <div className="border-t pt-4">
                <Label className="flex items-center gap-2 mb-3">
                  <CalendarRange className="h-4 w-4 text-secondary" />
                  Seasonal / Date-Range Pricing
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  Adjust pricing for specific date ranges (peak season, holidays, etc.)
                </p>
                
                <div className="space-y-3">
                  {(tripForm.seasonal_pricing || []).map((sp, idx) => (
                    <div key={idx} className="p-4 border rounded-lg bg-background">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                          <div>
                            <Label className="text-xs text-muted-foreground">Name</Label>
                            <Input
                              value={sp.name || ''}
                              onChange={(e) => {
                                const updated = [...(tripForm.seasonal_pricing || [])];
                                updated[idx] = { ...updated[idx], name: e.target.value };
                                setTripForm({ ...tripForm, seasonal_pricing: updated });
                              }}
                              placeholder="Peak Season"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Start Date</Label>
                            <Input
                              type="date"
                              value={sp.start_date || ''}
                              onChange={(e) => {
                                const updated = [...(tripForm.seasonal_pricing || [])];
                                updated[idx] = { ...updated[idx], start_date: e.target.value };
                                setTripForm({ ...tripForm, seasonal_pricing: updated });
                              }}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">End Date</Label>
                            <Input
                              type="date"
                              value={sp.end_date || ''}
                              onChange={(e) => {
                                const updated = [...(tripForm.seasonal_pricing || [])];
                                updated[idx] = { ...updated[idx], end_date: e.target.value };
                                setTripForm({ ...tripForm, seasonal_pricing: updated });
                              }}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Price Adjustment</Label>
                            <div className="flex items-center gap-1">
                              <select
                                value={sp.adjustment_type || 'dollar'}
                                onChange={(e) => {
                                  const updated = [...(tripForm.seasonal_pricing || [])];
                                  updated[idx] = { ...updated[idx], adjustment_type: e.target.value };
                                  setTripForm({ ...tripForm, seasonal_pricing: updated });
                                }}
                                className="text-sm border rounded px-2 py-1.5"
                              >
                                <option value="dollar">$</option>
                                <option value="percent">%</option>
                              </select>
                              <Input
                                type="number"
                                value={sp.adjustment_value || 0}
                                onChange={(e) => {
                                  const updated = [...(tripForm.seasonal_pricing || [])];
                                  updated[idx] = { ...updated[idx], adjustment_value: parseFloat(e.target.value) || 0 };
                                  setTripForm({ ...tripForm, seasonal_pricing: updated });
                                }}
                                placeholder="0"
                                className="text-sm w-20"
                              />
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const updated = (tripForm.seasonal_pricing || []).filter((_, i) => i !== idx);
                            setTripForm({ ...tripForm, seasonal_pricing: updated });
                          }}
                          className="text-destructive hover:text-destructive shrink-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs mt-2 text-muted-foreground">
                        {sp.start_date && sp.end_date ? (
                          <>
                            {sp.name || 'This period'}: {sp.adjustment_type === 'percent' 
                              ? `${sp.adjustment_value > 0 ? '+' : ''}${sp.adjustment_value}%`
                              : `${sp.adjustment_value > 0 ? '+' : ''}$${sp.adjustment_value}`} per person
                          </>
                        ) : 'Set date range to see pricing preview'}
                      </p>
                    </div>
                  ))}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newSeasonalPricing = {
                        id: `sp_${Date.now()}`,
                        name: '',
                        start_date: '',
                        end_date: '',
                        adjustment_type: 'dollar',
                        adjustment_value: 0
                      };
                      setTripForm({
                        ...tripForm,
                        seasonal_pricing: [...(tripForm.seasonal_pricing || []), newSeasonalPricing]
                      });
                    }}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Date Range Pricing
                  </Button>
                  
                  {(tripForm.seasonal_pricing || []).length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2 bg-muted/50 p-2 rounded">
                      💡 Seasonal adjustments are applied per person when trip dates fall within these ranges
                    </p>
                  )}
                </div>
              </div>
              
              {/* Tour Dates Section */}
              <div className="border-t pt-4">
                <Label className="flex items-center gap-2 mb-3">
                  <Calendar className="h-4 w-4 text-secondary" />
                  Tour Dates
                </Label>
                
                {/* Date Mode Options */}
                <div className="flex flex-wrap gap-4 mb-4 p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={tripForm.single_date_mode}
                      onCheckedChange={(checked) => {
                        setTripForm({ 
                          ...tripForm, 
                          single_date_mode: checked,
                          tour_dates: checked && tripForm.tour_dates?.length > 1 
                            ? [tripForm.tour_dates[0]] 
                            : tripForm.tour_dates
                        });
                      }}
                    />
                    <Label className="text-xs">Single Date Only</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={tripForm.date_display_mode || 'full'}
                      onChange={(e) => setTripForm({ ...tripForm, date_display_mode: e.target.value })}
                      className="text-xs border rounded px-2 py-1 bg-background"
                    >
                      <option value="full">Full Date (Jan 15, 2027)</option>
                      <option value="month_year">Month/Year Only (February 2027)</option>
                    </select>
                  </div>
                </div>
                
                {tripForm.date_display_mode === 'month_year' && (
                  <p className="text-xs text-amber-600 mb-3 bg-amber-50 p-2 rounded">
                    💡 Month/Year mode is great for events like Carnaval where exact dates aren't announced yet
                  </p>
                )}
                
                {/* Add new date */}
                <div className="flex gap-2 mb-3">
                  {tripForm.date_display_mode === 'month_year' ? (
                    <>
                      <select
                        id="new-tour-month"
                        className="flex-1 border rounded px-3 py-2 bg-background text-sm"
                      >
                        <option value="">Select Month</option>
                        <option value="01">January</option>
                        <option value="02">February</option>
                        <option value="03">March</option>
                        <option value="04">April</option>
                        <option value="05">May</option>
                        <option value="06">June</option>
                        <option value="07">July</option>
                        <option value="08">August</option>
                        <option value="09">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                      </select>
                      <select
                        id="new-tour-year"
                        className="w-24 border rounded px-3 py-2 bg-background text-sm"
                      >
                        <option value="">Year</option>
                        {[2025, 2026, 2027, 2028, 2029, 2030].map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          const month = document.getElementById('new-tour-month')?.value;
                          const year = document.getElementById('new-tour-year')?.value;
                          if (!month || !year) {
                            toast.error('Please select month and year');
                            return;
                          }
                          const dateStr = `${year}-${month}-01`;
                          if (tripForm.single_date_mode && tripForm.tour_dates?.length > 0) {
                            setTripForm({ ...tripForm, tour_dates: [dateStr] });
                          } else if (tripForm.tour_dates?.includes(dateStr)) {
                            toast.error('This date is already added');
                          } else {
                            setTripForm({ ...tripForm, tour_dates: [...(tripForm.tour_dates || []), dateStr].sort() });
                          }
                          document.getElementById('new-tour-month').value = '';
                          document.getElementById('new-tour-year').value = '';
                        }}
                        data-testid="add-tour-date-btn"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        {tripForm.single_date_mode ? 'Set Date' : 'Add Date'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Input
                        id="new-tour-date"
                        type="date"
                        className="flex-1"
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="new-tour-date-input"
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          if (tripForm.single_date_mode) {
                            const newDate = document.getElementById('new-tour-date')?.value;
                            if (!newDate) {
                              toast.error('Please select a date');
                              return;
                            }
                            setTripForm({ ...tripForm, tour_dates: [newDate] });
                            document.getElementById('new-tour-date').value = '';
                          } else {
                            handleAddTourDate();
                          }
                        }}
                        data-testid="add-tour-date-btn"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        {tripForm.single_date_mode ? 'Set Date' : 'Add Date'}
                      </Button>
                    </>
                  )}
                </div>
                
                {/* Current dates list */}
                {tripForm.tour_dates && tripForm.tour_dates.length > 0 ? (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {tripForm.tour_dates.map((date, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                        data-testid={`tour-date-${idx}`}
                      >
                        <span className="text-sm font-medium">
                          {tripForm.date_display_mode === 'month_year' 
                            ? (() => {
                                const [year, month] = date.split('-');
                                const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                                return `${monthNames[parseInt(month) - 1]} ${year}`;
                              })()
                            : formatTourDate(date)
                          }
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive h-7 w-7 p-0"
                          onClick={() => handleRemoveTourDate(date)}
                          data-testid={`remove-date-${idx}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No tour dates added yet</p>
                )}
              </div>

              {/* Location Variants Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-secondary" />
                    Location Variants
                  </Label>
                  {!showVariantInput ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowVariantInput(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Variant
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input
                        value={newVariantName}
                        onChange={(e) => setNewVariantName(e.target.value)}
                        placeholder="Location name (e.g., Cairo)"
                        className="h-8 w-40"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (!newVariantName.trim()) return;
                            const variantId = newVariantName.toLowerCase().replace(/\s+/g, '-');
                            const existingIds = (tripForm.location_variants || []).map(v => v.id);
                            if (existingIds.includes(variantId)) {
                              toast.error('A variant with this name already exists');
                              return;
                            }
                            const newVariant = {
                              id: variantId,
                              name: newVariantName.trim(),
                              description: '',
                              price_adjustment: 0,
                              has_group_dates: true,
                              itinerary: [],
                              add_ons: []
                            };
                            setTripForm({
                              ...tripForm,
                              location_variants: [...(tripForm.location_variants || []), newVariant]
                            });
                            toast.success(`Added "${newVariantName}" variant`);
                            setNewVariantName('');
                            setShowVariantInput(false);
                          } else if (e.key === 'Escape') {
                            setNewVariantName('');
                            setShowVariantInput(false);
                          }
                        }}
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          if (!newVariantName.trim()) {
                            setShowVariantInput(false);
                            return;
                          }
                          const variantId = newVariantName.toLowerCase().replace(/\s+/g, '-');
                          const existingIds = (tripForm.location_variants || []).map(v => v.id);
                          if (existingIds.includes(variantId)) {
                            toast.error('A variant with this name already exists');
                            return;
                          }
                          const newVariant = {
                            id: variantId,
                            name: newVariantName.trim(),
                            description: '',
                            price_adjustment: 0,
                            has_group_dates: true,
                            itinerary: [],
                            add_ons: []
                          };
                          setTripForm({
                            ...tripForm,
                            location_variants: [...(tripForm.location_variants || []), newVariant]
                          });
                          toast.success(`Added "${newVariantName}" variant`);
                          setNewVariantName('');
                          setShowVariantInput(false);
                        }}
                      >
                        Add
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setNewVariantName('');
                          setShowVariantInput(false);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                
                {tripForm.location_variants && tripForm.location_variants.length > 0 ? (
                  <>
                    <p className="text-xs text-muted-foreground mb-3">
                      Configure pricing and date availability for each location
                    </p>
                    
                    <div className="space-y-3">
                      {tripForm.location_variants.map((variant, idx) => (
                        <div 
                          key={variant.id} 
                          className="p-3 bg-muted/30 rounded-lg border"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex-1">
                              <Input
                                value={variant.name}
                                onChange={(e) => {
                                  const newVariants = [...tripForm.location_variants];
                                  newVariants[idx] = {
                                    ...newVariants[idx],
                                    name: e.target.value
                                  };
                                  setTripForm({ ...tripForm, location_variants: newVariants });
                                }}
                                className="h-7 text-sm font-medium w-48"
                                placeholder="Location name"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${variant.has_group_dates !== false ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {variant.has_group_dates !== false ? 'Group Dates' : 'Select Date'}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                onClick={() => {
                                  if (!window.confirm(`Delete "${variant.name}" variant? This cannot be undone.`)) return;
                                  const newVariants = tripForm.location_variants.filter((_, i) => i !== idx);
                                  setTripForm({ ...tripForm, location_variants: newVariants });
                                  toast.success(`Deleted "${variant.name}" variant`);
                                }}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          
                          <div className="mb-2">
                            <Input
                              value={variant.description || ''}
                              onChange={(e) => {
                                const newVariants = [...tripForm.location_variants];
                                newVariants[idx] = {
                                  ...newVariants[idx],
                                  description: e.target.value
                                };
                                setTripForm({ ...tripForm, location_variants: newVariants });
                              }}
                              className="h-7 text-xs"
                              placeholder="Short description (e.g., 'Famous for diving')"
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Price Adjustment ($)</Label>
                              <Input
                                type="number"
                                value={variant.price_adjustment || 0}
                                onChange={(e) => {
                                  const newVariants = [...tripForm.location_variants];
                                  newVariants[idx] = {
                                    ...newVariants[idx],
                                    price_adjustment: parseInt(e.target.value) || 0
                                  };
                                  setTripForm({ ...tripForm, location_variants: newVariants });
                                }}
                                className="h-8 text-sm"
                                placeholder="0 for base, -200 for discount"
                              />
                            </div>
                            <div className="flex items-end">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={variant.has_group_dates !== false}
                                  onCheckedChange={(checked) => {
                                    const newVariants = [...tripForm.location_variants];
                                    newVariants[idx] = {
                                      ...newVariants[idx],
                                      has_group_dates: checked
                                    };
                                    setTripForm({ ...tripForm, location_variants: newVariants });
                                  }}
                                />
                                <Label className="text-xs">Has Group Dates</Label>
                              </div>
                            </div>
                          </div>
                          
                          {variant.price_adjustment !== 0 && (
                            <p className="text-xs text-muted-foreground mt-2">
                              Final price: ${(tripForm.price || 0) + (variant.price_adjustment || 0)}
                            </p>
                          )}
                          
                          {/* Edit Itinerary Button */}
                          <div className="mt-3 pt-3 border-t flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={async () => {
                                // Save current form first
                                const savedTrip = await handleSaveTrip();
                                if (savedTrip) {
                                  // Use the variant from the saved trip to ensure we have latest data
                                  const freshVariant = savedTrip.location_variants?.find(v => v.id === variant.id) || variant;
                                  setEditingVariantItinerary(freshVariant);
                                }
                              }}
                            >
                              <Calendar className="h-4 w-4 mr-2" />
                              Edit Itinerary
                              {variant.itinerary?.length > 0 && (
                                <Badge variant="secondary" className="ml-2">{variant.itinerary.length} days</Badge>
                              )}
                            </Button>
                            {editingTrip?.itinerary?.length > 0 && (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                  if (variant.itinerary?.length > 0) {
                                    toast(`Replace existing ${variant.itinerary.length}-day itinerary?`, {
                                      action: {
                                        label: 'Yes, Import',
                                        onClick: () => {
                                          const newVariants = [...tripForm.location_variants];
                                          newVariants[idx] = {
                                            ...newVariants[idx],
                                            itinerary: JSON.parse(JSON.stringify(editingTrip.itinerary))
                                          };
                                          setTripForm({ ...tripForm, location_variants: newVariants });
                                          toast.success(`Imported ${editingTrip.itinerary.length}-day itinerary to ${variant.name}`);
                                        }
                                      }
                                    });
                                  } else {
                                    const newVariants = [...tripForm.location_variants];
                                    newVariants[idx] = {
                                      ...newVariants[idx],
                                      itinerary: JSON.parse(JSON.stringify(editingTrip.itinerary))
                                    };
                                    setTripForm({ ...tripForm, location_variants: newVariants });
                                    toast.success(`Imported ${editingTrip.itinerary.length}-day itinerary to ${variant.name}`);
                                  }
                                }}
                                title="Copy main trip itinerary to this variant"
                              >
                                <Copy className="h-4 w-4 mr-1" />
                                Import Main
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    No location variants. Add variants to offer this trip at multiple destinations.
                  </p>
                )}
              </div>

              {/* Accommodation Levels Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2">
                    <Hotel className="h-4 w-4 text-secondary" />
                    Accommodation Levels
                  </Label>
                  {!showAccommodationInput ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAccommodationInput(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Level
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input
                        value={newAccommodationName}
                        onChange={(e) => setNewAccommodationName(e.target.value)}
                        placeholder="e.g., Standard, Premium, Luxury"
                        className="h-8 w-44"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (!newAccommodationName.trim()) return;
                            const levelId = newAccommodationName.toLowerCase().replace(/\s+/g, '-');
                            const existingIds = (tripForm.accommodation_levels || []).map(l => l.id);
                            if (existingIds.includes(levelId)) {
                              toast.error('An accommodation level with this name already exists');
                              return;
                            }
                            const newLevel = {
                              id: levelId,
                              name: newAccommodationName.trim(),
                              description: '',
                              price_adjustment: 0,
                              itinerary: []
                            };
                            setTripForm({
                              ...tripForm,
                              accommodation_levels: [...(tripForm.accommodation_levels || []), newLevel]
                            });
                            toast.success(`Added "${newAccommodationName}" accommodation level`);
                            setNewAccommodationName('');
                            setShowAccommodationInput(false);
                          } else if (e.key === 'Escape') {
                            setNewAccommodationName('');
                            setShowAccommodationInput(false);
                          }
                        }}
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          if (!newAccommodationName.trim()) {
                            setShowAccommodationInput(false);
                            return;
                          }
                          const levelId = newAccommodationName.toLowerCase().replace(/\s+/g, '-');
                          const existingIds = (tripForm.accommodation_levels || []).map(l => l.id);
                          if (existingIds.includes(levelId)) {
                            toast.error('An accommodation level with this name already exists');
                            return;
                          }
                          const newLevel = {
                            id: levelId,
                            name: newAccommodationName.trim(),
                            description: '',
                            price_adjustment: 0,
                            itinerary: []
                          };
                          setTripForm({
                            ...tripForm,
                            accommodation_levels: [...(tripForm.accommodation_levels || []), newLevel]
                          });
                          toast.success(`Added "${newAccommodationName}" accommodation level`);
                          setNewAccommodationName('');
                          setShowAccommodationInput(false);
                        }}
                      >
                        Add
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setNewAccommodationName('');
                          setShowAccommodationInput(false);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                
                {tripForm.accommodation_levels && tripForm.accommodation_levels.length > 0 ? (
                  <>
                    <p className="text-xs text-muted-foreground mb-3">
                      Different accommodation tiers with unique pricing and itineraries (e.g., different hotels)
                    </p>
                    
                    <div className="space-y-3">
                      {tripForm.accommodation_levels.map((level, idx) => (
                        <div 
                          key={level.id} 
                          className="p-3 bg-amber-50/50 rounded-lg border border-amber-200"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex-1">
                              <Input
                                value={level.name}
                                onChange={(e) => {
                                  const newLevels = [...tripForm.accommodation_levels];
                                  newLevels[idx] = {
                                    ...newLevels[idx],
                                    name: e.target.value
                                  };
                                  setTripForm({ ...tripForm, accommodation_levels: newLevels });
                                }}
                                className="h-7 text-sm font-medium w-48"
                                placeholder="Level name"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                              onClick={() => {
                                if (!window.confirm(`Delete "${level.name}" accommodation level? This cannot be undone.`)) return;
                                const newLevels = tripForm.accommodation_levels.filter((_, i) => i !== idx);
                                setTripForm({ ...tripForm, accommodation_levels: newLevels });
                                toast.success(`Deleted "${level.name}" accommodation level`);
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          <div className="mb-2">
                            <Input
                              value={level.description || ''}
                              onChange={(e) => {
                                const newLevels = [...tripForm.accommodation_levels];
                                newLevels[idx] = {
                                  ...newLevels[idx],
                                  description: e.target.value
                                };
                                setTripForm({ ...tripForm, accommodation_levels: newLevels });
                              }}
                              className="h-7 text-xs"
                              placeholder="Description (e.g., '4-star hotels with breakfast included')"
                            />
                          </div>
                          
                          <div className="mb-3">
                            <Label className="text-xs">Price Adjustment ($)</Label>
                            <Input
                              type="number"
                              value={level.price_adjustment || 0}
                              onChange={(e) => {
                                const newLevels = [...tripForm.accommodation_levels];
                                newLevels[idx] = {
                                  ...newLevels[idx],
                                  price_adjustment: parseInt(e.target.value) || 0
                                };
                                setTripForm({ ...tripForm, accommodation_levels: newLevels });
                              }}
                              className="h-8 text-sm w-32"
                              placeholder="0 for base price"
                            />
                            {level.price_adjustment !== 0 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Final price: ${(tripForm.price || 0) + (level.price_adjustment || 0)}
                              </p>
                            )}
                          </div>
                          
                          {/* Traveler Requirements */}
                          <div className="mb-3 p-2 bg-slate-50 rounded-lg">
                            <Label className="text-xs flex items-center gap-1 mb-2">
                              <Users className="h-3 w-3" />
                              Traveler Requirements
                            </Label>
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <Label className="text-[10px] text-muted-foreground">Min</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  max="10"
                                  value={level.min_travelers || 1}
                                  onChange={(e) => {
                                    const newLevels = [...tripForm.accommodation_levels];
                                    newLevels[idx] = {
                                      ...newLevels[idx],
                                      min_travelers: parseInt(e.target.value) || 1
                                    };
                                    setTripForm({ ...tripForm, accommodation_levels: newLevels });
                                  }}
                                  className="h-7 text-xs w-16"
                                />
                              </div>
                              <div className="flex-1">
                                <Label className="text-[10px] text-muted-foreground">Max</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  max="10"
                                  value={level.max_travelers || 10}
                                  onChange={(e) => {
                                    const newLevels = [...tripForm.accommodation_levels];
                                    newLevels[idx] = {
                                      ...newLevels[idx],
                                      max_travelers: parseInt(e.target.value) || 10
                                    };
                                    setTripForm({ ...tripForm, accommodation_levels: newLevels });
                                  }}
                                  className="h-7 text-xs w-16"
                                />
                              </div>
                              <p className="text-[10px] text-muted-foreground flex-1">
                                {level.min_travelers === level.max_travelers 
                                  ? `Only for ${level.min_travelers} traveler${level.min_travelers > 1 ? 's' : ''}`
                                  : (level.max_travelers || 10) >= 10
                                    ? `${level.min_travelers || 1}+ travelers`
                                    : `${level.min_travelers || 1}-${level.max_travelers} travelers`
                                }
                              </p>
                            </div>
                          </div>
                          
                          {/* Edit Itinerary Button */}
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={async () => {
                                // Save current form first
                                const savedTrip = await handleSaveTrip();
                                if (savedTrip) {
                                  // Use the level from the saved trip to ensure we have latest data
                                  const freshLevel = savedTrip.accommodation_levels?.find(l => l.id === level.id) || level;
                                  setEditingAccommodationItinerary(freshLevel);
                                }
                              }}
                            >
                              <Calendar className="h-4 w-4 mr-2" />
                              Edit Itinerary
                              {level.itinerary?.length > 0 && (
                                <Badge variant="secondary" className="ml-2">{level.itinerary.length} days</Badge>
                              )}
                            </Button>
                            {editingTrip?.itinerary?.length > 0 && (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                  // Show confirmation via toast action instead of window.confirm
                                  if (level.itinerary?.length > 0) {
                                    toast(`Replace existing ${level.itinerary.length}-day itinerary?`, {
                                      action: {
                                        label: 'Yes, Import',
                                        onClick: () => {
                                          const newLevels = [...tripForm.accommodation_levels];
                                          newLevels[idx] = {
                                            ...newLevels[idx],
                                            itinerary: JSON.parse(JSON.stringify(editingTrip.itinerary))
                                          };
                                          setTripForm({ ...tripForm, accommodation_levels: newLevels });
                                          toast.success(`Imported ${editingTrip.itinerary.length}-day itinerary to ${level.name}`);
                                        }
                                      }
                                    });
                                  } else {
                                    // No existing itinerary, import directly
                                    const newLevels = [...tripForm.accommodation_levels];
                                    newLevels[idx] = {
                                      ...newLevels[idx],
                                      itinerary: JSON.parse(JSON.stringify(editingTrip.itinerary))
                                    };
                                    setTripForm({ ...tripForm, accommodation_levels: newLevels });
                                    toast.success(`Imported ${editingTrip.itinerary.length}-day itinerary to ${level.name}`);
                                  }
                                }}
                                title="Copy main trip itinerary to this level"
                              >
                                <Copy className="h-4 w-4 mr-1" />
                                Import Main
                              </Button>
                            )}
                          </div>
                          
                          {/* Traveler-Based Itinerary Variations */}
                          <div className="mt-3 pt-3 border-t border-amber-200">
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-xs flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                Traveler-Based Itinerary Variations
                              </Label>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs"
                                onClick={() => {
                                  const newLevels = [...tripForm.accommodation_levels];
                                  const existingTiers = newLevels[idx].traveler_itineraries || [];
                                  newLevels[idx] = {
                                    ...newLevels[idx],
                                    traveler_itineraries: [
                                      ...existingTiers,
                                      {
                                        id: `tier-${Date.now()}`,
                                        min_travelers: 1,
                                        max_travelers: 4,
                                        itinerary: []
                                      }
                                    ]
                                  };
                                  setTripForm({ ...tripForm, accommodation_levels: newLevels });
                                }}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Add Tier
                              </Button>
                            </div>
                            
                            {level.traveler_itineraries && level.traveler_itineraries.length > 0 ? (
                              <div className="space-y-2">
                                {level.traveler_itineraries.map((tier, tierIdx) => (
                                  <div 
                                    key={tier.id} 
                                    className="p-2 bg-white rounded border border-slate-200 text-xs"
                                  >
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="text-muted-foreground">Travelers:</span>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={tier.min_travelers || 1}
                                        onChange={(e) => {
                                          const newLevels = [...tripForm.accommodation_levels];
                                          const newTiers = [...newLevels[idx].traveler_itineraries];
                                          newTiers[tierIdx] = {
                                            ...newTiers[tierIdx],
                                            min_travelers: parseInt(e.target.value) || 1
                                          };
                                          newLevels[idx] = { ...newLevels[idx], traveler_itineraries: newTiers };
                                          setTripForm({ ...tripForm, accommodation_levels: newLevels });
                                        }}
                                        className="h-6 w-14 text-xs"
                                      />
                                      <span>to</span>
                                      <Input
                                        type="number"
                                        min="1"
                                        value={tier.max_travelers || 10}
                                        onChange={(e) => {
                                          const newLevels = [...tripForm.accommodation_levels];
                                          const newTiers = [...newLevels[idx].traveler_itineraries];
                                          newTiers[tierIdx] = {
                                            ...newTiers[tierIdx],
                                            max_travelers: parseInt(e.target.value) || 10
                                          };
                                          newLevels[idx] = { ...newLevels[idx], traveler_itineraries: newTiers };
                                          setTripForm({ ...tripForm, accommodation_levels: newLevels });
                                        }}
                                        className="h-6 w-14 text-xs"
                                      />
                                      <span className="text-muted-foreground">people</span>
                                      
                                      <div className="flex-1" />
                                      
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="h-6 text-xs"
                                        onClick={async () => {
                                          const savedTrip = await handleSaveTrip();
                                          if (savedTrip) {
                                            const freshLevel = savedTrip.accommodation_levels?.find(l => l.id === level.id);
                                            const freshTier = freshLevel?.traveler_itineraries?.find(t => t.id === tier.id);
                                            if (freshTier) {
                                              setEditingAccommodationItinerary({
                                                ...freshLevel,
                                                _tierEdit: freshTier,
                                                _tierIdx: tierIdx
                                              });
                                            }
                                          }
                                        }}
                                      >
                                        <Calendar className="h-3 w-3 mr-1" />
                                        Edit ({tier.itinerary?.length || 0} days)
                                      </Button>
                                      
                                      {/* Import Main Itinerary to Tier */}
                                      {editingTrip?.itinerary?.length > 0 && (
                                        <Button
                                          type="button"
                                          variant="secondary"
                                          size="sm"
                                          className="h-6 text-xs"
                                          onClick={() => {
                                            if (tier.itinerary?.length > 0) {
                                              toast(`Replace existing ${tier.itinerary.length}-day itinerary for this tier?`, {
                                                action: {
                                                  label: 'Yes, Import',
                                                  onClick: () => {
                                                    const newLevels = [...tripForm.accommodation_levels];
                                                    const newTiers = [...newLevels[idx].traveler_itineraries];
                                                    newTiers[tierIdx] = {
                                                      ...newTiers[tierIdx],
                                                      itinerary: JSON.parse(JSON.stringify(editingTrip.itinerary))
                                                    };
                                                    newLevels[idx] = { ...newLevels[idx], traveler_itineraries: newTiers };
                                                    setTripForm({ ...tripForm, accommodation_levels: newLevels });
                                                    toast.success(`Imported ${editingTrip.itinerary.length}-day itinerary to tier`);
                                                  }
                                                }
                                              });
                                            } else {
                                              const newLevels = [...tripForm.accommodation_levels];
                                              const newTiers = [...newLevels[idx].traveler_itineraries];
                                              newTiers[tierIdx] = {
                                                ...newTiers[tierIdx],
                                                itinerary: JSON.parse(JSON.stringify(editingTrip.itinerary))
                                              };
                                              newLevels[idx] = { ...newLevels[idx], traveler_itineraries: newTiers };
                                              setTripForm({ ...tripForm, accommodation_levels: newLevels });
                                              toast.success(`Imported ${editingTrip.itinerary.length}-day itinerary to tier`);
                                            }
                                          }}
                                          title="Copy main trip itinerary to this tier"
                                        >
                                          <Copy className="h-3 w-3 mr-1" />
                                          Import
                                        </Button>
                                      )}
                                      
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 text-destructive"
                                        onClick={() => {
                                          const newLevels = [...tripForm.accommodation_levels];
                                          const newTiers = newLevels[idx].traveler_itineraries.filter((_, i) => i !== tierIdx);
                                          newLevels[idx] = { ...newLevels[idx], traveler_itineraries: newTiers };
                                          setTripForm({ ...tripForm, accommodation_levels: newLevels });
                                        }}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-[10px] text-muted-foreground italic">
                                No traveler tiers. The default itinerary above will be used for all group sizes.
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    No accommodation levels. Add levels to offer different hotel tiers with unique itineraries.
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditingTrip(null)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveTrip}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Variant Itinerary Editor */}
      {editingVariantItinerary && editingTrip && (
        <div className="fixed inset-0 bg-background z-50 overflow-auto">
          <div className="container max-w-5xl py-6">
            <ItineraryEditor
              trip={editingTrip}
              variant={editingVariantItinerary}
              onBack={() => setEditingVariantItinerary(null)}
              onSave={async () => {
                // Fetch fresh trip data to get the updated itinerary
                try {
                  const res = await axios.get(`${API}/admin/trips`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  const freshTrips = res.data;
                  const freshTrip = freshTrips.find(t => t.id === editingTrip.id);
                  
                  if (freshTrip) {
                    // Update editingTrip with fresh data
                    setEditingTrip(freshTrip);
                    
                    // Update tripForm.location_variants with fresh data
                    setTripForm(prev => ({
                      ...prev,
                      location_variants: freshTrip.location_variants || []
                    }));
                  }
                } catch (err) {
                  console.error('Failed to refresh trip data:', err);
                }
                
                onRefresh();
                setEditingVariantItinerary(null);
              }}
            />
          </div>
        </div>
      )}
      
      {/* Accommodation Level Itinerary Editor */}
      {editingAccommodationItinerary && editingTrip && (
        <div className="fixed inset-0 bg-background z-50 overflow-auto">
          <div className="container max-w-5xl py-6">
            <ItineraryEditor
              trip={editingTrip}
              accommodationLevel={editingAccommodationItinerary}
              onBack={() => setEditingAccommodationItinerary(null)}
              onSave={async () => {
                // Fetch fresh trip data to get the updated itinerary
                try {
                  const res = await axios.get(`${API}/admin/trips`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  const freshTrips = res.data;
                  const freshTrip = freshTrips.find(t => t.id === editingTrip.id);
                  
                  if (freshTrip) {
                    // Update editingTrip with fresh data
                    setEditingTrip(freshTrip);
                    
                    // Update tripForm.accommodation_levels with fresh data
                    setTripForm(prev => ({
                      ...prev,
                      accommodation_levels: freshTrip.accommodation_levels || []
                    }));
                  }
                } catch (err) {
                  console.error('Failed to refresh trip data:', err);
                }
                
                onRefresh();
                setEditingAccommodationItinerary(null);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
